import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { API_URL } from './constants';
import { getAvailability, subscribeApiAvailability } from './apiAvailability';

const SHOW_DELAY_MS = 800;
const HEALTH_POLL_MS = 8000;

export default function ApiMaintenanceNotice() {
  const [availability, setAvailability] = useState(getAvailability);
  const [visible, setVisible] = useState(false);
  const previousStatus = useRef(availability.status);
  const showedNotice = useRef(false);

  useEffect(() => subscribeApiAvailability(setAvailability), []);

  useEffect(() => {
    if (availability.status === 'up') return undefined;
    const timer = setInterval(() => setAvailability(getAvailability()), 1000);
    return () => clearInterval(timer);
  }, [availability.status]);

  useEffect(() => {
    if (availability.status !== 'maintenance') {
      setVisible(false);
      return undefined;
    }
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [availability.status, availability.downSince]);

  useEffect(() => {
    if (availability.status !== 'maintenance') return undefined;
    let cancelled = false;
    const ping = async () => {
      try {
        await fetch(`${API_URL}/version`, { cache: 'no-store' });
      } catch {
        if (!cancelled) setAvailability(getAvailability());
      }
    };
    ping();
    const timer = setInterval(ping, HEALTH_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [availability.status]);

  useEffect(() => {
    if (visible) showedNotice.current = true;
  }, [visible]);

  useEffect(() => {
    const previous = previousStatus.current;
    previousStatus.current = availability.status;
    if (previous === 'maintenance' && availability.status === 'up' && showedNotice.current) {
      showedNotice.current = false;
      window.location.reload();
    }
  }, [availability.status]);

  if (!visible) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 20000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(244, 247, 246, 0.97)',
        p: 3,
      }}
    >
      <Box sx={{ maxWidth: 560, textAlign: 'center' }}>
        <CircularProgress size={28} sx={{ mb: 2, color: '#0f6b4c' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
          Scheduled maintenance is underway, platform will be available again in a few minutes
        </Typography>
      </Box>
    </Box>
  );
}
